import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { Observable, of } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';
import { AuthStateService } from 'src/app/core/services/auth-state.service';
import { AuthService } from 'src/app/data/api/services';

/**
 * Short-lived cache of the auth check so a burst of guarded navigations does not fire one
 * HTTP round-trip each. Token expiry is still caught by the 401 handling in ErrorService
 * (which redirects to login), so a stale `true` only survives at most AUTH_CACHE_TTL_MS.
 */
const AUTH_CACHE_TTL_MS = 30_000;
let cachedAuth$: Observable<boolean> | null = null;
let cachedAt = 0;

/** Test-only: the module-level cache leaks between specs (jasmine runs them in random
 *  order within one browser session), so each test must start from a cold cache. */
export function resetAuthGuardCacheForTesting(): void {
  cachedAuth$ = null;
  cachedAt = 0;
}

export const authGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const authState = inject(AuthStateService);
  const document = inject(DOCUMENT);

  const redirectToLogin = () => {
    document.location.href = `/api/auth/login?returnUrl=${encodeURIComponent(document.location.href)}`;
    return false as const;
  };

  const now = Date.now();
  if (!cachedAuth$ || now - cachedAt > AUTH_CACHE_TTL_MS) {
    cachedAt = now;
    cachedAuth$ = authService.isAuthenticated({}).pipe(shareReplay({ bufferSize: 1, refCount: false }));
  }

  return cachedAuth$.pipe(
    map((authenticated) => {
      if (authenticated) {
        authState.confirm();
        return true;
      }
      return redirectToLogin();
    }),
    catchError(() => {
      // Don't cache a failed check — the next navigation should re-validate.
      cachedAuth$ = null;
      return of(redirectToLogin());
    }),
  );
};
